import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Globe2, ArrowRight, BookOpen, GraduationCap, Award, Target, Users, FileText, Mountain, Layers, Video } from 'lucide-react';
import { useSiteContent } from '../contexts/SiteContentContext';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const iconMap = { BookOpen, GraduationCap, Award, Target, Users, Globe2, FileText, Mountain, Layers, Video };

export default function CustomPage() {
  const { pageId, slug } = useParams();
  const content = useSiteContent();

  if (!content) return <div className="min-h-screen flex items-center justify-center bg-[#F4F7F6]"><div className="w-10 h-10 border-4 border-[#0077B6] border-t-transparent rounded-full animate-spin" /></div>;

  // Find page by pageId or by slug in menu_config
  let actualPageId = pageId;
  if (!actualPageId && slug) {
    const menuConfig = content.menu_config;
    if (menuConfig && menuConfig.items) {
      const menuItem = menuConfig.items.find(m => m.slug === slug);
      if (menuItem) actualPageId = menuItem.id;
    }
  }
  if (!actualPageId) return <div className="min-h-screen flex items-center justify-center bg-[#F4F7F6]"><p className="text-[#5C6B7A]">Səhifə tapılmadı</p></div>;

  const pg = content[actualPageId] || {};
  const heroBadge = pg.hero_badge || '';
  const heroTitle = pg.hero_title || 'Səhifə';
  const heroSubtitle = pg.hero_subtitle || '';
  const heroBg = pg.hero_bg || '';
  const sections = pg.sections || [];

  return (
    <div>
      {/* Hero */}
      <section className="relative py-28 md:py-36 overflow-hidden">
        <div className="absolute inset-0">
          {heroBg ? <img src={heroBg} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-gradient-to-br from-[#03045E] via-[#0077B6] to-[#00B4D8]" />}
          <div className="absolute inset-0 bg-gradient-to-t from-[#03045E]/40 to-transparent" />
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            {heroBadge && <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 text-white text-sm font-bold mb-6 border border-white/20"><Globe2 className="w-4 h-4" /> {heroBadge}</span>}
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6">{heroTitle}</h1>
            {heroSubtitle && <p className="text-lg md:text-xl text-white font-semibold max-w-2xl mx-auto">{heroSubtitle}</p>}
          </motion.div>
        </div>
      </section>

      {/* Dynamic Sections */}
      {sections.map((sec, i) => {
        if (sec.type === 'text') return (
          <section key={i} className="py-16 md:py-24 bg-white">
            <div className="max-w-4xl mx-auto px-6 md:px-12">
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                {sec.title && <h2 className="text-3xl md:text-4xl font-bold text-[#1A2530] mb-6">{sec.title}</h2>}
                {sec.content && (
                  <div className="[&_h2]:text-2xl [&_h2]:font-bold [&_h2]:text-[#1A2530] [&_h2]:mt-6 [&_h2]:mb-3 [&_h3]:text-xl [&_h3]:font-bold [&_h3]:text-[#0077B6] [&_h3]:mt-4 [&_h3]:mb-2 [&_p]:text-[#5C6B7A] [&_p]:leading-relaxed [&_p]:mb-3 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:text-[#5C6B7A] [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:text-[#5C6B7A] [&_strong]:text-[#1A2530]">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{sec.content}</ReactMarkdown>
                  </div>
                )}
              </motion.div>
            </div>
          </section>
        );

        if (sec.type === 'cards') return (
          <section key={i} className="py-16 md:py-24 bg-[#F4F7F6]">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
              {sec.title && <h2 className="text-3xl md:text-4xl font-bold text-[#1A2530] mb-8 text-center">{sec.title}</h2>}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {(sec.items || []).map((card, ci) => {
                  const Icon = iconMap[card.icon] || BookOpen;
                  return (
                    <motion.div key={ci} className="bg-white rounded-2xl p-8 border border-[#E2E8F0] card-hover"
                      initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: ci * 0.08 }}>
                      <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#0077B6] to-[#00B4D8] flex items-center justify-center mb-4">
                        <Icon className="w-7 h-7 text-white" />
                      </div>
                      <h3 className="text-lg font-bold text-[#1A2530] mb-2">{card.title}</h3>
                      <p className="text-sm text-[#5C6B7A] leading-relaxed">{card.desc}</p>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </section>
        );

        if (sec.type === 'gallery') return (
          <section key={i} className="py-16 md:py-24 bg-white">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
              {sec.title && <h2 className="text-3xl md:text-4xl font-bold text-[#1A2530] mb-8 text-center">{sec.title}</h2>}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {(sec.images || []).map((img, gi) => (
                  <motion.div key={gi} className="rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-shadow"
                    initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}>
                    <img src={img} alt="" className="w-full h-48 object-cover hover:scale-105 transition-transform duration-500" />
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        );

        if (sec.type === 'cta') return (
          <section key={i} className="py-16 md:py-24 bg-gradient-to-r from-[#0077B6] to-[#00B4D8]">
            <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">{sec.title}</h2>
                {sec.subtitle && <p className="text-lg text-white/80 mb-8">{sec.subtitle}</p>}
                <Link to={sec.button_url || '/contact'} className="inline-flex items-center gap-2 px-8 py-4 rounded-2xl bg-white text-[#0077B6] font-bold hover:shadow-lg transition-all group">
                  {sec.button_text || 'Əlaqə'} <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </motion.div>
            </div>
          </section>
        );

        if (sec.type === 'books') {
          const statusMap = { coming: { label: 'Tezliklə', color: 'bg-amber-500' }, available: { label: 'Mövcuddur', color: 'bg-green-500' }, new: { label: 'Yeni nəşr', color: 'bg-[#0077B6]' } };
          const parentSlug = content.menu_config?.items?.find(m => m.id === actualPageId)?.slug || actualPageId;
          return (
            <section key={i} className="py-16 md:py-24 bg-[#F4F7F6]">
              <div className="max-w-7xl mx-auto px-6 md:px-12">
                {sec.title && <h2 className="text-3xl md:text-4xl font-bold text-[#1A2530] mb-10 text-center">{sec.title}</h2>}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {(sec.items || []).map((book, bi) => (
                    <motion.div key={bi} className="group bg-white rounded-3xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-500"
                      initial={{ opacity: 0, y: 25 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: bi * 0.1 }}>
                      <Link to={`/${parentSlug}/${book.slug || bi}`}>
                      {/* Cover Image */}
                      <div className="relative h-80 bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center overflow-hidden">
                        {book.image ? (
                          <img src={book.image} alt={book.title} className="h-full w-auto object-contain group-hover:scale-105 transition-transform duration-500 drop-shadow-xl" />
                        ) : (
                          <div className="text-center">
                            <BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-2" />
                            <p className="text-sm text-slate-400">Üz qabığı</p>
                          </div>
                        )}
                        {/* Status Badge */}
                        {book.status && (
                          <div className={`absolute top-4 right-4 px-3 py-1.5 rounded-full text-white text-xs font-bold shadow-lg ${statusMap[book.status]?.color || 'bg-slate-500'}`}>
                            {statusMap[book.status]?.label || book.status}
                          </div>
                        )}
                      </div>
                      </Link>
                      {/* Content */}
                      <div className="p-6">
                        <Link to={`/${parentSlug}/${book.slug || bi}`}>
                          <h3 className="text-xl font-bold text-[#1A2530] mb-1 hover:text-[#0077B6] transition-colors">{book.title || 'Kitab adı'}</h3>
                        </Link>
                        {book.author && <p className="text-sm text-[#0077B6] font-medium mb-3">{book.author}</p>}
                        {(book.short_desc || book.desc) && <p className="text-sm text-[#5C6B7A] leading-relaxed mb-4 line-clamp-3">{(book.short_desc || book.desc).replace(/#{1,3}\s+[^\n]*/g, '').replace(/\*\*/g, '').replace(/\n{2,}/g, ' ').replace(/- /g, '').trim()}</p>}
                        <div className="flex items-center justify-between">
                          {book.price && <span className="text-lg font-bold text-[#1A2530]">{book.price}</span>}
                          {book.url ? (
                            <a href={book.url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#0077B6] to-[#00B4D8] text-white text-sm font-bold hover:shadow-lg transition-all group">
                              Sifariş üçün <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </a>
                          ) : (
                            <span className="text-sm text-[#5C6B7A] italic">{book.status === 'coming' ? 'Tezliklə mövcud olacaq' : ''}</span>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </section>
          );
        }

        return null;
      })}
    </div>
  );
}
